import React from 'react';
import { Users, Target, Heart, Award } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const AboutUs = () => {
  const values = [
    {
      icon: Heart,
      title: 'Quality First',
      description: 'We carefully select every toy to ensure safety and durability for endless fun.',
      color: '#fad24b',
    },
    {
      icon: Users,
      title: 'Customer Love',
      description: 'Our customers are family. We go above and beyond to make every kid smile.',
      color: '#3dd3ee',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Always hunting for the newest, coolest toys that spark creativity and joy.',
      color: '#ff8c19',
    },
    {
      icon: Award,
      title: 'Trust & Safety',
      description: 'All products are tested and certified safe for children of all ages.',
      color: '#b4dc19',
    },
  ];

  return (
    <section id="about" className="py-20 px-[5%]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block font-extrabold text-xs tracking-[2px] uppercase mb-[10px]"
            style={{ color: 'var(--pink-d)' }}
          >
            Why Thinkkool
          </span>
          <h2
            className="fredoka mb-2"
            style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
              color: 'var(--dark)',
            }}
          >
            Parents Trust Us 🌸
          </h2>
        </div>

        {/* Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative">
            <div className="relative w-full h-[400px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/8617585/pexels-photo-8617585.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Kids playing with fidget toys"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/50 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#fad24b] rounded-full opacity-70 blur-2xl"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#3dd3ee] rounded-full opacity-70 blur-2xl"></div>
          </div>

          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-[#1e1919]">
              Our Story
            </h3>
            <p className="text-[#736c64] leading-relaxed">
              Founded with a simple mission: to make every child's day a little brighter. What started as a small collection of fidget toys has grown into a beloved destination for parents and kids seeking quality, fun, and innovative toys.
            </p>
            <p className="text-[#736c64] leading-relaxed">
              Every product in our store is hand-selected and tested to ensure it meets our high standards for safety, quality, and most importantly - FUN! We believe in the power of play to reduce stress, improve focus, and spark creativity.
            </p>
            <div className="flex flex-wrap gap-8 pt-6">
              <div>
                <div className="text-3xl font-bold text-[#ff8c19]">2020</div>
                <div className="text-sm text-[#736c64]">Founded</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#3dd3ee]">500+</div>
                <div className="text-sm text-[#736c64]">Happy Families</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#fad24b]">50+</div>
                <div className="text-sm text-[#736c64]">Unique Products</div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Gallery Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          <div className="relative h-[280px] rounded-[20px] overflow-hidden shadow-lg group">
            <img
              src="https://images.pexels.com/photos/33997860/pexels-photo-33997860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Colorful pastel plush toys"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4">
              <h4 className="text-white font-bold text-xl">Adorable Squishes</h4>
              <p className="text-white/90 text-sm">Soft & satisfying</p>
            </div>
          </div>
          
          <div className="relative h-[280px] rounded-[20px] overflow-hidden shadow-lg group">
            <img
              src="https://images.pexels.com/photos/11477550/pexels-photo-11477550.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Rainbow pop it fidget toy"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4">
              <h4 className="text-white font-bold text-xl">Fun Fidgets</h4>
              <p className="text-white/90 text-sm">Endless entertainment</p>
            </div>
          </div>
          
          <div className="relative h-[280px] rounded-[20px] overflow-hidden shadow-lg group">
            <img
              src="https://images.unsplash.com/photo-1758598737977-094cb3ca9ce8?w=800&q=80"
              alt="Woman and child building with blocks"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4">
              <h4 className="text-white font-bold text-xl">Creative Play</h4>
              <p className="text-white/90 text-sm">Building memories</p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <div
                key={index}
                className="text-center p-8 rounded-[20px] transition-all duration-300 hover:scale-105"
                style={{
                  background: 'white',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: value.color }}
                >
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h4
                  className="text-xl font-bold mb-2"
                  style={{ color: 'var(--dark)' }}
                >
                  {value.title}
                </h4>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--mid)' }}
                >
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
